import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useSeason } from './SeasonContext';

const formatLastFive = (teamId, games) => {
    if (!games || games.length === 0) return [];
    return games
        .sort((a, b) => new Date(a.game_date) - new Date(b.game_date))
        .map(game => {
            const isTeam1 = game.team1_id === teamId;
            const teamScore = isTeam1 ? game.team1_score : game.team2_score;
            const opponentScore = isTeam1 ? game.team2_score : game.team1_score;
            return teamScore > opponentScore ? 'W' : 'L';
        });
};

export const GameContext = createContext({});

export const useGame = () => {
    const context = useContext(GameContext);
    if (!context) throw new Error('useGame must be used within GameProvider');
    return context;
};

export const GameProvider = ({ children }) => {
    const { activeSeason, selectedSeason } = useSeason();
    const [activeGame, setActiveGame] = useState(null);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);

    const [gameId, setGameId] = useState(null);
    const [liveGameId, setLiveGameId] = useState(null);
    const [finishedGamesForStandings, setFinishedGamesForStandings] = useState([]);

    const gameIdRef = useRef(gameId);
    useEffect(() => { gameIdRef.current = gameId; }, [gameId]);

    const liveGameIdRef = useRef(liveGameId);
    useEffect(() => { liveGameIdRef.current = liveGameId; }, [liveGameId]);

    const activeGameRef = useRef(activeGame);
    useEffect(() => { activeGameRef.current = activeGame; }, [activeGame]);

    const syncTimerRef = useRef(null);

    // ─── Data loading ──────────────────────────────────────────────────────────

    const loadTeams = useCallback(async () => {
        const seasonId = selectedSeason?.id;
        if (!seasonId) return;

        const { data: teamsData, error } = await supabase
            .from('teams')
            .select('*')
            .eq('season_id', seasonId)
            .is('archived_at', null)
            .order('wins', { ascending: false });

        if (error || !teamsData) return;

        const teamsWithStreaks = await Promise.all(teamsData.map(async (team) => {
            const { data: games } = await supabase
                .from('games')
                .select('team1_id, team2_id, team1_score, team2_score, game_date')
                .or(`team1_id.eq.${team.id},team2_id.eq.${team.id}`)
                .eq('status', 'finished')
                .order('game_date', { ascending: false })
                .limit(5);

            return { ...team, last_5: formatLastFive(team.id, games) };
        }));

        setTeams(teamsWithStreaks);
    }, [selectedSeason]);

    const loadPlayers = useCallback(async () => {
        const seasonId = selectedSeason?.id;
        if (!seasonId) return;

        const { data, error } = await supabase
            .from('players')
            .select('*')
            .eq('season_id', seasonId)
            .not('team_id', 'is', null);

        if (!error && data) setPlayers(data);
    }, [selectedSeason]);

    const loadFinishedGames = useCallback(async () => {
        const seasonId = selectedSeason?.id;
        if (!seasonId) return;

        const { data } = await supabase
            .from('games')
            .select('id, team1_id, team2_id, team1_score, team2_score')
            .eq('season_id', seasonId)
            .eq('status', 'finished')
            .eq('game_type', 'regular');

        setFinishedGamesForStandings(data || []);
    }, [selectedSeason]);

    useEffect(() => {
        if (selectedSeason) {
            loadTeams();
            loadPlayers();
            loadFinishedGames();
        }
    }, [selectedSeason, loadTeams, loadPlayers, loadFinishedGames]);

    // ─── Head-to-head + standings ──────────────────────────────────────────────

    const getHeadToHeadWins = useCallback((teamAId, teamBId) => {
        let wins = 0;
        finishedGamesForStandings.forEach(game => {
            const isTeam1A = game.team1_id === teamAId;
            const isTeam2A = game.team2_id === teamAId;
            const isTeam1B = game.team1_id === teamBId;
            const isTeam2B = game.team2_id === teamBId;

            if ((isTeam1A && isTeam2B) || (isTeam2A && isTeam1B)) {
                const scoreA = isTeam1A ? game.team1_score : game.team2_score;
                const scoreB = isTeam1A ? game.team2_score : game.team1_score;
                if (scoreA > scoreB) wins++;
            }
        });
        return wins;
    }, [finishedGamesForStandings]);

    const sortedStandings = useMemo(() => {
        if (!teams.length) return [];
        return [...teams].sort((a, b) => {
            const totalA = a.wins + a.losses;
            const totalB = b.wins + b.losses;
            const pctA = totalA > 0 ? a.wins / totalA : 0;
            const pctB = totalB > 0 ? b.wins / totalB : 0;
            if (pctA !== pctB) return pctB - pctA;

            const h2hA = getHeadToHeadWins(a.id, b.id);
            const h2hB = getHeadToHeadWins(b.id, a.id);
            if (h2hA !== h2hB) return h2hB - h2hA;

            if ((a.point_diff ?? 0) !== (b.point_diff ?? 0)) {
                return (b.point_diff ?? 0) - (a.point_diff ?? 0);
            }
            return 0;
        });
    }, [teams, getHeadToHeadWins]);

    /// ─── Live game sync ──────────────────────────────────────────────────────────
    const syncLiveGame = useCallback((lastAction = null) => {
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);

        syncTimerRef.current = setTimeout(async () => {
            const id = liveGameIdRef.current;
            const game = activeGameRef.current;
            if (!id || !game) return;

            const update = {
                team1_score: game.team1Score,
                team2_score: game.team2Score,
                half: game.half,
                status: 'live',
            };

            if (lastAction) update.last_action = lastAction;

            try {
                await supabase
                    .from('live_games')
                    .update(update)
                    .eq('id', id);
            } catch (_err) {
                // Best-effort
            }
        }, 800);
    }, []);

    // ─── Stat upsert ──────────────────────────────────────────────────────────
    const upsertPlayerStat = useCallback(async (gId, player) => {
        if (!gId) return;
        try {
            await supabase
                .from('game_stats')
                .upsert({
                    game_id: gId,
                    player_id: player.id,
                    points: player.gamePoints ?? 0,
                    three_pm: player.gameThreePm ?? 0,
                    two_pm: player.gameTwoPm ?? 0,
                    ft_made: player.gameFtMade ?? 0,
                    rebounds: player.gameRebounds ?? 0,
                    assists: player.gameAssists ?? 0,
                    personal_fouls: player.gamePersonalFouls ?? 0,
                    technical_fouls: player.gameTechnicalFouls ?? 0,
                }, { onConflict: 'game_id,player_id' });
        } catch (err) {
            console.warn('Stat upsert failed (non-fatal):', err);
        }
    }, []);

    // ─── Game lifecycle ────────────────────────────────────────────────────────
    const initPlayerStats = (player) => ({
        ...player,
        gamePoints: 0,
        gameRebounds: 0,
        gameAssists: 0,
        gamePersonalFouls: 0,
        gameTechnicalFouls: 0,
        gameThreePm: 0,
        gameTwoPm: 0,
        gameFtMade: 0,
    });

    const takeTimeout = (teamNum) => {
        setActiveGame(prev => {
            if (!prev) return prev;

            const key = teamNum === 1 ? 'team1TimeoutsLeft' : 'team2TimeoutsLeft';

            let newVal = (prev[key] ?? 0) - 1;

            // OT rule: if already 0 and we're in OT → grant 1
            if (prev.half > 2 && prev[key] === 0) {
                newVal = 0; // consume OT timeout immediately
            }

            return {
                ...prev,
                [key]: Math.max(0, newVal)
            };
        });

        syncLiveGame({ type: 'timeout', team: teamNum });
    };

    // ─── Add player to team on the fly ────────────────────────────────────────────────────────
    const addPlayerToGame = useCallback(async (teamId, name, jerseyNumber) => {
        const seasonId = activeSeason?.id;
        if (!seasonId) throw new Error('No active season');

        const { data, error } = await supabase
            .from('players')
            .insert({
                team_id: teamId,
                season_id: seasonId,
                name: name.trim(),
                jersey_number: jerseyNumber || null,
            })
            .select()
            .single();

        if (error) throw error;

        // Also inject into activeGame with zeroed stats so LiveGame
        // doesn't receive a raw DB row missing game stat fields
        setActiveGame(prev => {
            if (!prev) return prev;
            const isTeam1 = String(prev.team1?.id) === String(teamId);
            const playerList = isTeam1 ? 'team1Players' : 'team2Players';
            return {
                ...prev,
                [playerList]: [
                    ...prev[playerList],
                    {
                        ...data,
                        gamePoints: 0,
                        gameRebounds: 0,
                        gameAssists: 0,
                        gamePersonalFouls: 0,
                        gameTechnicalFouls: 0,
                        gameThreePm: 0,
                        gameTwoPm: 0,
                        gameFtMade: 0,
                    },
                ],
            };
        });

        return data;
    }, [activeSeason]);

    /**
     * startGame
     *
     * existingGameId — pass the games.id when starting a scheduled game.
     *   The row already exists with status='scheduled'; we patch it to 'live'.
     *   When null, a fresh games row is created (manual / scrimmage start).
     */
    const startGame = async (team1Id, team2Id, isScrimmage = false, existingGameId = null) => {
        const team1 = teams.find(t => t.id === team1Id);
        const team2 = teams.find(t => t.id === team2Id);

        let newGameId = null;

        if (existingGameId) {
            // Scheduled game path — patch existing row to 'live'
            try {
                const { data: { session } } = await supabase.auth.getSession();
                const scorerId = session?.user?.id ?? null;
                
                const { error } = await supabase
                    .from('games')
                    .update({ status: 'live', scorer_id: scorerId })
                    .eq('id', existingGameId);

                if (!error) newGameId = existingGameId;
                else console.error('Failed to activate scheduled game:', error);
            } catch (err) {
                console.error('Failed to activate scheduled game:', err);
            }
        } else {
            // Manual / scrimmage path — create a fresh games row
            try {
                const now = new Date();
                const y = now.getFullYear();
                const m = String(now.getMonth() + 1).padStart(2, '0');
                const d = String(now.getDate()).padStart(2, '0');
                const hh = String(now.getHours()).padStart(2, '0');
                const mm = String(now.getMinutes()).padStart(2, '0');
                const ss = String(now.getSeconds()).padStart(2, '0');
                const localNow = `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
                const { data: { session } } = await supabase.auth.getSession();
                const scorerId = session?.user?.id ?? null;

                const { data: gameData, error: gameErr } = await supabase
                    .from('games')
                    .insert({
                        team1_id: team1Id,
                        team2_id: team2Id,
                        team1_score: 0,
                        team2_score: 0,
                        status: 'live',
                        game_type: isScrimmage ? 'scrimmage' : 'regular',
                        season_id: activeSeason.id,
                        game_date: localNow,
                        scorer_id: scorerId,
                    })
                    .select('id')
                    .single();

                if (!gameErr && gameData) newGameId = gameData.id;
                else console.error('Failed to create games row at start:', gameErr);
            } catch (err) {
                console.error('Failed to create games row at start:', err);
            }
        }

        // Insert live_games row — link game_id immediately
        let newLiveGameId = null;
        try {
            const { data: liveData, error: liveErr } = await supabase
                .from('live_games')
                .insert({
                    team1_id: team1Id,
                    team2_id: team2Id,
                    team1_score: 0,
                    team2_score: 0,
                    half: 1,
                    status: 'live',
                    is_scrimmage: isScrimmage,
                    game_id: newGameId, // properly linked from the start
                })
                .select('id')
                .single();

            if (!liveErr && liveData) newLiveGameId = liveData.id;
        } catch (_err) {
            // Non-fatal
        }

        setGameId(newGameId);
        setLiveGameId(newLiveGameId);

        // Always fetch fresh players for this game directly from DB — don't rely on context players being loaded
        try {
            const [{ data: t1PlayersRaw = [] }, { data: t2PlayersRaw = [] }] = await Promise.all([
                supabase
                    .from('players')
                    .select('*')
                    .eq('team_id', team1Id)
                    .not('team_id', 'is', null),
                supabase
                    .from('players')
                    .select('*')
                    .eq('team_id', team2Id)
                    .not('team_id', 'is', null),
            ]);

            const team1Players = t1PlayersRaw.map(initPlayerStats);
            const team2Players = t2PlayersRaw.map(initPlayerStats);

            setActiveGame({
                team1,
                team2,
                team1Score: 0,
                team2Score: 0,
                half: 1,
                isScrimmage,
                gameType: isScrimmage ? 'scrimmage' : 'regular',
                team1Players,
                team2Players,
                team1FoulsH1: 0,
                team1FoulsH2: 0,
                team2FoulsH1: 0,
                team2FoulsH2: 0,
                team1TimeoutsLeft: 3,
                team2TimeoutsLeft: 3,
            });
        } catch (err) {
            console.error('Failed to load players for new game:', err);
            // Fallback to global players (rare)
            setActiveGame({
                team1,
                team2,
                team1Score: 0,
                team2Score: 0,
                half: 1,
                isScrimmage,
                gameType: isScrimmage ? 'scrimmage' : 'regular',
                team1Players: players.filter(p => p.team_id === team1Id).map(initPlayerStats),
                team2Players: players.filter(p => p.team_id === team2Id).map(initPlayerStats),
                team1FoulsH1: 0,
                team1FoulsH2: 0,
                team2FoulsH1: 0,
                team2FoulsH2: 0,
                team1TimeoutsLeft: 3,
                team2TimeoutsLeft: 3,
            });
        }
    };

    /**
     * resumeGame — restores a stranded live game from DB state.
     * Fetches live_games row + existing game_stats and reconstructs
     * activeGame exactly where the scorer left off.
     */
    const resumeGame = async (liveGameRow) => {
        const { id: liveId, game_id: existingGameId, team1_id, team2_id,
            team1_score, team2_score, half, is_scrimmage } = liveGameRow; 

        let team1 = teams.find(t => t.id === team1_id);
        let team2 = teams.find(t => t.id === team2_id);

        // Fallback if teams context not yet loaded
        if (!team1 || !team2) {
            const { data: teamRows } = await supabase
                .from('teams')
                .select('*')
                .in('id', [team1_id, team2_id]);

            team1 = team1 || teamRows?.find(t => t.id === team1_id);
            team2 = team2 || teamRows?.find(t => t.id === team2_id);
        }

        // Fetch players directly — don't rely on context players being loaded
        const [{ data: t1PlayersRaw }, { data: t2PlayersRaw }] = await Promise.all([
            supabase.from('players').select('*').eq('team_id', team1_id).not('team_id', 'is', null),
            supabase.from('players').select('*').eq('team_id', team2_id).not('team_id', 'is', null),
        ]);

        // Fetch existing stats from DB keyed by player_id
        let statsByPlayerId = {};
        if (existingGameId) {
            const { data: existingStats } = await supabase
                .from('game_stats')
                .select('*')
                .eq('game_id', existingGameId);

            (existingStats || []).forEach(s => {
                statsByPlayerId[s.player_id] = s;
            });
        }

        const restorePlayer = (player) => {
            const s = statsByPlayerId[player.id];
            return {
                ...player,
                gamePoints: s?.points ?? 0,
                gameRebounds: s?.rebounds ?? 0,
                gameAssists: s?.assists ?? 0,
                gamePersonalFouls: s?.personal_fouls ?? 0,
                gameTechnicalFouls: s?.technical_fouls ?? 0,
                gameThreePm: s?.three_pm ?? 0,
                gameTwoPm: s?.two_pm ?? 0,
                gameFtMade: s?.ft_made ?? 0,
            };
        };

        const team1Players = (t1PlayersRaw || []).map(restorePlayer);
        const team2Players = (t2PlayersRaw || []).map(restorePlayer);

        const sumPF = (playerList) =>
            playerList.reduce((acc, p) => acc + (p.gamePersonalFouls ?? 0), 0);

        setGameId(existingGameId);
        setLiveGameId(liveId);

        setActiveGame({
            team1,
            team2,
            team1Score: team1_score,
            team2Score: team2_score,
            half,
            isScrimmage: is_scrimmage,
            gameType: is_scrimmage ? 'scrimmage' : 'regular',
            team1Players,
            team2Players,
            team1FoulsH1: half === 1 ? sumPF(team1Players) : 0,
            team1FoulsH2: half === 2 ? sumPF(team1Players) : 0,
            team2FoulsH1: half === 1 ? sumPF(team2Players) : 0,
            team2FoulsH2: half === 2 ? sumPF(team2Players) : 0,
            team1TimeoutsLeft: 3,
            team2TimeoutsLeft: 3,
        });
    };

    const recordStat = async (teamNum, playerId, stat, points = 0, statDelta = null) => {
        let updatedPlayer = null;

        // Build lastAction from current ref state — no need to wait for setState
        const currentGame = activeGameRef.current;
        const effectiveDelta = statDelta !== null ? statDelta : (points !== 0 ? points : 1);
        let lastAction = null;

        if (currentGame && effectiveDelta > 0) {
            const playerList = teamNum === 1 ? currentGame.team1Players : currentGame.team2Players;
            const player = playerList?.find(p => p.id === playerId);
            if (player) {
                lastAction = {
                    player_name: player.name,
                    jersey: player.jersey_number,
                    team_name: teamNum === 1 ? currentGame.team1?.name : currentGame.team2?.name,
                    stat,
                    points,
                    ts: new Date().toISOString(),
                };
            }
        }

        setActiveGame(prev => {
            if (!prev) return null;

            const newGame = { ...prev };
            const playerList = teamNum === 1 ? 'team1Players' : 'team2Players';
            const scoreKey = teamNum === 1 ? 'team1Score' : 'team2Score';

            newGame[playerList] = newGame[playerList].map(p => {
                if (p.id === playerId) {
                    const statKey = `game${stat.charAt(0).toUpperCase() + stat.slice(1)}`;
                    const delta = statDelta !== null ? statDelta : (points !== 0 ? points : 1);
                    const updated = { ...p, [statKey]: Math.max(0, (p[statKey] ?? 0) + delta) };
                    updatedPlayer = updated;
                    return updated;
                }
                return p;
            });

            if (points !== 0) {
                newGame[scoreKey] = Math.max(0, newGame[scoreKey] + points);
            }

            if (stat === 'personalFouls') {
                const half = newGame.half;

                let teamFoulKey;

                if (half === 1) {
                    teamFoulKey = teamNum === 1 ? 'team1FoulsH1' : 'team2FoulsH1';
                } else if (half === 2) {
                    teamFoulKey = teamNum === 1 ? 'team1FoulsH2' : 'team2FoulsH2';
                } else {
                    // Overtime → dynamic bucket
                    teamFoulKey = teamNum === 1
                        ? `team1FoulsOT${half - 2}`
                        : `team2FoulsOT${half - 2}`;
                }

                const delta = statDelta !== null ? statDelta : 1;

                newGame[teamFoulKey] = Math.max(0, (newGame[teamFoulKey] ?? 0) + delta);
            }

            return newGame;
        });

        syncLiveGame(lastAction);

        setTimeout(() => {
            if (updatedPlayer && gameIdRef.current) {
                upsertPlayerStat(gameIdRef.current, updatedPlayer);
            }
        }, 0);
    };

    const advanceHalf = () => {
        setActiveGame(prev => {
            if (!prev) return prev;
            return { ...prev, half: prev.half + 1 };
        });
        syncLiveGame();
    };

    const finishGame = async () => {
        if (!activeGame) return;
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            return {
                success: false,
                error: {
                    message:
                        'You are not logged in. Please refresh and log in again. ' +
                        'Stats have been saved to the database on every tap — they are safe.'
                }
            };
        }

        const currentGameId = gameIdRef.current;

        try {
            if (!currentGameId) {
                throw new Error(
                    'No game ID found. The initial games row may have failed to create. ' +
                    'Use the admin Game Results tab to record this game manually.'
                );
            }

            // 1. Patch existing games row to finished with final scores
            const { error: updateErr } = await supabase
                .from('games')
                .update({
                    team1_score: activeGame.team1Score,
                    team2_score: activeGame.team2Score,
                    status: 'finished',
                })
                .eq('id', currentGameId);

            if (updateErr) throw updateErr;

            // 2. Final stat flush — ensures last tap is durable
            const allPlayers = [
                ...activeGame.team1Players,
                ...activeGame.team2Players,
            ];
            await Promise.all(allPlayers.map(p => upsertPlayerStat(currentGameId, p)));

            // 3. Recalculate W/L + point_diff from scratch (regular games only)
            if (!activeGame.isScrimmage) {
                for (const team of [activeGame.team1, activeGame.team2]) {
                    const { data: allGames, error: gamesErr } = await supabase
                        .from('games')
                        .select('team1_id, team2_id, team1_score, team2_score')
                        .eq('season_id', activeSeason.id)
                        .eq('game_type', 'regular')
                        .eq('status', 'finished')
                        .or(`team1_id.eq.${team.id},team2_id.eq.${team.id}`);

                    if (gamesErr) throw gamesErr;

                    let wins = 0, losses = 0, pointDiff = 0;
                    for (const g of allGames) {
                        const isTeam1 = g.team1_id === team.id;
                        const teamScore = isTeam1 ? g.team1_score : g.team2_score;
                        const oppScore = isTeam1 ? g.team2_score : g.team1_score;
                        if (teamScore > oppScore) wins++;
                        else if (teamScore < oppScore) losses++;
                        pointDiff += teamScore - oppScore;
                    }

                    const { error: wlErr } = await supabase
                        .from('teams')
                        .update({ wins, losses, point_diff: pointDiff })
                        .eq('id', team.id);

                    if (wlErr) throw wlErr;
                }
            }

            // 4. Mark live_games as final
            if (liveGameIdRef.current) {
                try {
                    await supabase
                        .from('live_games')
                        .update({
                            status: 'final',
                            game_id: currentGameId,
                            team1_score: activeGame.team1Score,
                            team2_score: activeGame.team2Score,
                            updated_at: new Date().toISOString(),
                        })
                        .eq('id', liveGameIdRef.current);
                } catch (err) {
                    console.warn('Live game finalization failed (non-critical):', err);
                }
            }

            await loadTeams();

            setActiveGame(null);
            setGameId(null);
            setLiveGameId(null);

            return { success: true };
        } catch (error) {
            console.error('Error finishing game:', error);
            return { success: false, error };
        }
    };

    const value = {
        activeGame,
        teams,
        players,
        gameId,
        liveGameId,
        addPlayerToGame,
        startGame,
        resumeGame,
        recordStat,
        advanceHalf,
        finishGame,
        loadTeams,
        loadPlayers,
        sortedStandings,
        finishedGamesForStandings,
        takeTimeout,
    };

    return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
